import { combineReducers } from '@reduxjs/toolkit';
import pick from 'lodash/pick';
import {
  persistStore,
  persistReducer,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import FetchInterceptor from 'fetch-interceptor';
import { CookieStorage } from 'redux-persist-cookie-storage';
import Cookies from 'cookies-js';
import { configureStore } from '@reduxjs/toolkit';
import { reducer as coreReducer, SET_SERVER_ERROR } from './modules/core';
import { reducer as authReducer, logout, USER_LOADED } from './modules/auth';
import { reducer as audioReducer } from './modules/audio';
import { reducer as assetReducer } from './modules/assets';
import { reducer as auditionReducer } from './modules/auditions';
import { reducer as auditionsTalentsReducer } from './modules/auditions-talents';
import { reducer as auditionsProducersReducer } from './modules/auditions-producers';
import { reducer as auditionsInvitationsReducer } from './modules/auditions-invitations';
import { reducer as auditionApplicationReducer } from './modules/audition-applications';
import { reducer as auditionSuggestionsReducer } from './modules/audition-suggestions';
import { reducer as accountReducer } from './modules/accounts';
import { reducer as projectsReducer } from './modules/projects';
import { reducer as producerReducer } from './modules/producers';
import { reducer as talentReducer } from './modules/talents';
import { reducer as listsReducer } from './modules/lists';
import { reducer as pastRecordingsReducer } from './modules/past-recordings';
import { reducer as projectRolesReducer } from './modules/project-roles';
import { reducer as topCandidatesReducer } from './modules/top-candidates';
import { reducer as notificationReducer } from './modules/notifications';
import { reducer as submissionsReducer } from './modules/submissions';
import { reducer as auditionMessageReducer } from './modules/audition-messages';
import { reducer as audioClipReducer } from './modules/audio-clips';
import { reducer as agentsReducer } from './modules/agents';
import { reducer as managersReducer } from './modules/managers';
import { reducer as sharingReducer } from './modules/sharing';
import { reducer as offersReducer } from './modules/offers';
import { reducer as chatReducer } from './modules/chat';
import { reducer as globalMessagingThreadsReducer } from './modules/global-messages-threads';
import { reducer as globalMessagingReducer } from './modules/global-messages';

const persistWhitelist = ['auth', 'accounts'];

const persistConfig = {
  key: 'root',
  storage: new CookieStorage(Cookies),
  whitelist: persistWhitelist.filter(path => !path.includes('.')),
  transforms: [
    createTransform((inboundState, key) => {
      const nestedWhitelistState = persistWhitelist
        .filter(path => path.startsWith(`${key}.`))
        .map(path => path.substring(key.length + 1));

      return pick(inboundState, ...nestedWhitelistState);
    }, null),
  ],
};

/* keep auth first; other reducers must be alphabetically/logically ordered: parent resource reducer (auditions) precedes child resource reducer (auditionApplications) despite the alphabet */
const appReducer = combineReducers({
  core: coreReducer,
  auth: authReducer,
  accounts: accountReducer,
  agents: agentsReducer,
  assets: assetReducer,
  audio: audioReducer,
  audioClips: audioClipReducer,
  auditions: auditionReducer,
  auditionApplications: auditionApplicationReducer,
  auditionsInvitations: auditionsInvitationsReducer,
  auditionSuggestions: auditionSuggestionsReducer,
  auditionMessages: auditionMessageReducer,
  auditionsProducers: auditionsProducersReducer,
  auditionsTalents: auditionsTalentsReducer,
  chat: chatReducer,
  globalMessaging: globalMessagingReducer,
  globalMessagingThreads: globalMessagingThreadsReducer,
  lists: listsReducer,
  managerAssignments: managersReducer,
  notifications: notificationReducer,
  offers: offersReducer,
  pastRecordings: pastRecordingsReducer,
  producers: producerReducer,
  projects: projectsReducer,
  projectRoles: projectRolesReducer,
  submissions: submissionsReducer,
  sharing: sharingReducer,
  talents: talentReducer,
  topCandidates: topCandidatesReducer,
});

const rootReducer = (state, action) => {
  if (state !== undefined) {
    // Temporary solution for 503 error
    if (
      state.core.serverError === true &&
      action.type === 'auth/logout/fulfilled'
    ) {
      const temporaryState = state;
      // eslint-disable-next-line
      state = undefined;
      // eslint-disable-next-line
      state = { core: (serverError = temporaryState.core.serverError) };

      return appReducer(state, action);
    }
  }

  if (action.type === 'auth/logout/fulfilled') {
    // eslint-disable-next-line
    state = undefined;
  }

  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export default () => {
  const store = configureStore({
    reducer: persistedReducer,
    devTools: process.env.NODE_ENV !== 'production',
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            FLUSH,
            REHYDRATE,
            PAUSE,
            PERSIST,
            PURGE,
            REGISTER,
            USER_LOADED,
          ],
          // TODO: figure out how to store the AWS Cognito user result as serializable data
          ignoredPaths: ['auth.cognitoUser'],
        },
      }),
  });

  const persistor = persistStore(store);

  /* eslint-disable no-unused-vars */
  const interceptor = FetchInterceptor.register({
    onBeforeRequest(request, controller) {},
    onRequestFailure(response, request, controller) {
      const { dispatch } = store;

      if (response.status === 503) {
        dispatch({ type: SET_SERVER_ERROR });
        dispatch(logout());
      }

      if (response.status === 401) {
        dispatch(logout());
      }
    },
    onRequestSuccess(response, request, controller) {},
  });
  /* eslint-enable no-unused-vars */

  return { store, persistor, interceptor };
};
